import { podBulkUploadApi } from '@/api/services'
import {
  BULK_UPLOAD_S3_CONCURRENCY,
  BULK_UPLOAD_S3_DELAY_MS,
} from '@/utils/bulkUploadConfig'
import {
  groupFilesByDocumentNo,
  type BulkUploadResumeFileState,
  type BulkUploadResumeStatus,
} from '@/utils/bulkUploadResume'
import { clearActiveRun, persistActiveRun } from '@/utils/bulkUploadRunSession'

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

async function asyncPool<T, R>(limit: number, items: T[], fn: (item: T) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length)
  let next = 0
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++
      results[i] = await fn(items[i])
    }
  })
  await Promise.all(workers)
  return results
}

export type PipelineProgress = {
  current: number
  total: number
  percent: number
  currentDocumentNo: string | null
  succeededDocs: number
  failedDocs: number
  skippedDocs: number
}

export type RunBulkUploadPipelineArgs = {
  selectedFiles: File[]
  documentType: string
  batchId: string
  folderKey: string
  resume: BulkUploadResumeStatus
  uploadFile: (file: File) => Promise<string | null>
  shouldAbort: () => boolean
  onProgress: (progress: PipelineProgress) => void
  onDocumentComplete?: (batchId: string, processedDocs: number) => void
}

export type PipelineResult = {
  failedDocs: number
  succeededDocs: number
  skippedDocs: number
  aborted: boolean
}

function patchResumeForDoc(
  resume: BulkUploadResumeStatus,
  docNo: string,
  files: File[],
  procBody: { skipped?: boolean; failed?: boolean; status?: string }
) {
  const terminal = procBody.skipped || (!procBody.failed && procBody.status !== 'FAILED')
  for (const file of files) {
    const prev = resume.files[file.name]
    if (prev?.skip) continue

    if (terminal) {
      resume.files[file.name] = {
        ...prev,
        state: 'done',
        skip: true,
        status: procBody.status ?? 'COMPLETED',
        documentNo: docNo,
      } as BulkUploadResumeFileState
      resume.counts.done++
      if (!prev || prev.state === 'new') resume.counts.new = Math.max(0, resume.counts.new - 1)
      else if (prev.state === 'retry') resume.counts.retry = Math.max(0, resume.counts.retry - 1)
    } else if (procBody.failed) {
      resume.files[file.name] = {
        ...prev,
        state: 'retry',
        skip: false,
        status: 'FAILED',
        documentNo: docNo,
      } as BulkUploadResumeFileState
    }
  }
}

export async function runBulkUploadPipeline(args: RunBulkUploadPipelineArgs): Promise<PipelineResult> {
  const {
    selectedFiles,
    documentType,
    batchId,
    folderKey,
    resume,
    uploadFile,
    shouldAbort,
    onProgress,
    onDocumentComplete,
  } = args

  const docGroups = groupFilesByDocumentNo(selectedFiles)
  const totalDocs = docGroups.size
  let processedDocs = 0
  let failedDocs = 0
  let succeededDocs = 0
  let skippedDocs = 0

  const emit = (currentDocumentNo: string | null) => {
    onProgress({
      current: processedDocs,
      total: totalDocs,
      percent: totalDocs > 0 ? Math.round((processedDocs / totalDocs) * 100) : 0,
      currentDocumentNo,
      succeededDocs,
      failedDocs,
      skippedDocs,
    })
    persistActiveRun({
      batchId,
      documentType,
      folderKey,
      totalDocs,
      processedDocs,
      startedAt: Date.now(),
    })
  }

  emit(null)

  for (const [docNo, files] of docGroups) {
    if (shouldAbort()) {
      return { failedDocs, succeededDocs, skippedDocs, aborted: true }
    }

    const needsWork = files.some(f => !resume.files[f.name]?.skip)
    if (!needsWork) {
      processedDocs++
      skippedDocs++
      emit(docNo)
      onDocumentComplete?.(batchId, processedDocs)
      continue
    }

    emit(docNo)

    const filesToUpload = files.filter(f => {
      const fr = resume.files[f.name]
      return !fr?.skip && (!fr?.fileUrl || fr?.needsUpload)
    })

    const uploadedByName = new Map<string, string | null>()

    if (filesToUpload.length > 0) {
      await asyncPool(BULK_UPLOAD_S3_CONCURRENCY, filesToUpload, async (file) => {
        if (shouldAbort()) return
        if (BULK_UPLOAD_S3_DELAY_MS > 0) await sleep(BULK_UPLOAD_S3_DELAY_MS)
        const url = await uploadFile(file)
        uploadedByName.set(file.name, url)
      })
    }

    if (shouldAbort()) {
      return { failedDocs, succeededDocs, skippedDocs, aborted: true }
    }

    const payloads: Array<{ document_no: string; file_name: string; file_url: string | null }> = []
    for (const file of files) {
      const fr = resume.files[file.name]
      if (fr?.skip) continue
      const fileUrl = uploadedByName.get(file.name) ?? fr?.fileUrl ?? null
      payloads.push({ document_no: docNo, file_name: file.name, file_url: fileUrl })
    }

    const procRes: any = await podBulkUploadApi.processDocument({
      batchId,
      documentNo: docNo,
      documentType,
      files: payloads.length > 0 ? payloads : undefined,
    })
    const procBody = procRes?.data?.data ?? procRes?.data

    if (procBody?.failed || procBody?.status === 'FAILED') {
      failedDocs++
    } else {
      succeededDocs++
    }

    patchResumeForDoc(resume, docNo, files, procBody ?? {})

    if (procBody?.rateLimitHit) {
      await sleep(5000)
    }

    processedDocs++
    emit(null)
    onDocumentComplete?.(batchId, processedDocs)
  }

  clearActiveRun()
  return { failedDocs, succeededDocs, skippedDocs, aborted: false }
}
